// components/AppLayout.jsx
import React, { useRef, useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { useSearchTools } from '../hooks/useSearchTools';
import { useResultsTools } from '../hooks/useResultsTools';
import { useLoadBookingTools } from '../hooks/useLoadBookingTools';

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  // Lifted loads state, seeded from navigation state
  const [loads, setLoads] = useState(() => location.state?.loads || []);
  
  // Current load data for booking page
  const [currentLoadData, setCurrentLoadData] = useState(() => location.state?.loadData || null);

  // Shared refs for Search inputs & Results cards
  const originRef = useRef(null);
  const destinationRef = useRef(null);
  const dateRef = useRef(null);
  const equipmentRef = useRef(null);
  const loadRefs = useRef({});

  // Search tools wiring
  const {
    setOriginTool,
    setDestinationTool,
    setDateTool,
    setEquipmentTool,
    performSearch,
  } = useSearchTools({
    originRef,
    destinationRef,
    dateRef,
    equipmentRef,
    navigate,
    setOrigin: v => originRef.current && (originRef.current.value = v),
    setDestination: v => destinationRef.current && (destinationRef.current.value = v),
    setDate: v => dateRef.current && (dateRef.current.value = v),
    setEquipment: v => equipmentRef.current && (equipmentRef.current.value = v),
    setHighlightField: () => {}, // no-op
    onResults: newLoads => {
      setLoads(newLoads);
      navigate('/results', { state: { loads: newLoads } });
    }
  });

  // Results tools wiring - with custom handling for load booking
  const {
    getTopLoadsTool,
    getTopLoadsData,
    scrollToLoadTool,
    clickBookLoadTool,//: originalClickBookLoadTool,
  } = useResultsTools({
    navigate,
    toast,
    loadRefs,
    loads
  });
  
  // // Override the clickBookLoadTool to store the current load data
  // const clickBookLoadTool = ({ id }) => {
  //   // Find the load by ID
  //   // const loadData = loads.find(load => load.id === id);
  //   // const loadData = loads[id-1]//.find(load => load.id === id)
  //   localStorage.setItem('bookLoadIndex', id)
  //   // if (loadData) {
  //   //   // Store current load data for booking tools
  //   //   setCurrentLoadData(loadData);
      
  //   //   // Use the original tool to navigate
  //   //   return originalClickBookLoadTool({ id });
  //   // } else {
  //   //   toast({
  //   //     title: "Load not found",
  //   //     status: "error",
  //   //     duration: 3000,
  //   //     isClosable: true,
  //   //   });
  //   //   return { id, success: false };
  //   // }
  // };

  // Load booking tools wiring
  const {
    setLowerRateTool,
    setUpperRateTool,
    negotiateRateTool,
    callBrokerTool,
    emailBrokerTool,
    backToResultsTool,
  } = useLoadBookingTools({
    navigate,
    toast,
    loadData: currentLoadData
  });

  // Update current load data when location changes
  useEffect(() => {
    if (location.state?.loadData) {
      setCurrentLoadData(location.state.loadData);
    }
  }, [location.state]);

  // Single stable listener exposing ALL tools at once, regardless of current route
  const onCallRef = useRef();
  const widgetRef = useRef(null);  // Add ref to store widget reference

  if (!onCallRef.current) {
    onCallRef.current = event => {
      // Make all tools available all the time
      event.detail.config.clientTools = {
        // Search tools
        setOrigin: setOriginTool,
        setDestination: setDestinationTool,
        setDate: setDateTool,
        setEquipment: setEquipmentTool,
        performSearch,
        
        // Results tools
        getTopLoads: getTopLoadsTool,
        top_loads_data:getTopLoadsData,
        scrollToLoad: scrollToLoadTool,
        clickBookLoad: clickBookLoadTool,
        
        // Booking tools
        setLowerRate: setLowerRateTool,
        setUpperRate: setUpperRateTool,
        negotiateRate: negotiateRateTool,
        callBroker: callBrokerTool,
        emailBroker: emailBrokerTool,
        backToResults: backToResultsTool,
      };
    };
  }

  // Bind once on mount, cleanup on unmount
  // Since the widget might not exist on load of the application, check if not exist 
  useEffect(() => {
    const checkForWidget = () => {
      const widget = document.querySelector('elevenlabs-convai');
      if (widget) {
        widgetRef.current = widget;  // Store widget reference
        widget.addEventListener('elevenlabs-convai:call', onCallRef.current);
        return true;
      }
      return false;
    };

    if (!checkForWidget()) {
      // Check every 1 second until the widget exists
      const intervalId = setInterval(() => {
        if (checkForWidget()) {
          clearInterval(intervalId);
        }
      }, 1000);

      return () => {
        clearInterval(intervalId);
        if (widgetRef.current) {
          widgetRef.current.removeEventListener('elevenlabs-convai:call', onCallRef.current);
        }
      };
    }

    return () => {
      if (widgetRef.current) {
        widgetRef.current.removeEventListener('elevenlabs-convai:call', onCallRef.current);
      }
    };
  }, []);

  return (
    <Outlet context={{
      originRef,
      destinationRef,
      dateRef,
      equipmentRef,
      loadRefs,
      loads,
      currentLoadData,
    }} />
  );
}